/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Typography,
  Box,
  Autocomplete,
} from "@mui/material";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import CloseIcon from "@mui/icons-material/Close";
import {
  setGlobalFlexMetaDataChipList,
  setSubmissionAPIdata,
  setSubmissionFlexMetadataIds,
} from "../../Services/redux/global-admin-users/flex metadata/GlobalAdminFlexActions";
import { useDispatch, useSelector } from "react-redux";
import APIManager from "../../Services/ApiManager/ApiManager";
import { getConfig } from "../../config";

// Constants
const NAME_PATTERN = /^[A-Za-z0-9_-]+$/;
const GLOBAL_ADMIN_PORTAL = "global-admin";
const METADATA_DOMAIN = "NONE";
const ERROR_MESSAGE = "No spaces or special characters allowed except for - and _";
const REQUIRED_FIELD_COLOR = "#E53935";
const LISTBOX_MAX_HEIGHT = 142;
const TEXTAREA_MIN_ROWS = 3;
const BORDER_COLOR = "#E0E0E0";
const BUTTON_BG_COLOR = "#0070f3";
const CANCEL_BG_COLOR = "#f5f5f5";

function AddFlexMetaDataDialog({
  appId,
  submissionId,
  open,
  onClose,
  onSave,
  onDuplicate,
  flexMetaDataState = [],
  flexNameOptions, 
  isEditMode = false,
  initialName = "",
  initialDescription = "",
}) {
  const dispatch = useDispatch();
  const isGlobalAdmin = getConfig().REACT_APP_PORTAL === "global-admin";
  const {
    flexMetadata: globalFlexMetadata = [],
    globalFlexMetaDataChipList = [],
    submissionAPIdata = {},
    flexMetadataIds = [],
  } = useSelector((state) => state.globalAdminFlexReducers || {});
  const [flexMetaName, setFlexMetaName] = useState(initialName);
  const [flexDesc, setFlexDesc] = useState(initialDescription);

  // Helper: Check if current portal is global admin
  const isGlobalAdminPortal = useCallback(() => {
    return sessionStorage.getItem("customPortal") === GLOBAL_ADMIN_PORTAL;
  }, []);

  // Build options from either explicit prop or provided state
  const nameOptions = (
    flexNameOptions && Array.isArray(flexNameOptions)
      ? flexNameOptions
      : (flexMetaDataState || []).map((o) => o?.title || o?.name || "")
  ).filter(Boolean);

  // Selection + validation state
  const existingNames = nameOptions.map((n) => (n || "").toLowerCase());
  const nameHasValue = (flexMetaName || "").trim().length > 0;
  const isExistingSelection =
    !isEditMode && nameHasValue
      ? existingNames.includes((flexMetaName || "").trim().toLowerCase())
      : false;
  const isNamePatternValid = nameHasValue
    ? NAME_PATTERN.test(flexMetaName || "")
    : false;
  const nameErrorText =
    !isEditMode && nameHasValue && !isExistingSelection && !isNamePatternValid
      ? ERROR_MESSAGE
      : "";

  // Map metadata name to description from Global Admin slice
  const metadataByName = useMemo(() => {
    return (globalFlexMetadata || []).reduce((acc, item) => {
      const n = item?.name || item?.title;
      if (n) acc[n] = item?.description || "";
      return acc;
    }, {});
  }, [globalFlexMetadata]);

  // When selecting an existing name, auto-fill description (read-only)
  useEffect(() => {
    if (isExistingSelection && flexMetaName) {
      const desc = metadataByName[flexMetaName] || "";
      setFlexDesc(desc);
    }
  }, [isExistingSelection, flexMetaName, metadataByName]);

  const createGlobalFlexMetaChips = async (
    updatedSubmissionAPIdata,
    updatedFlexMetaChipList,
    updatedFlexMetadataIds
  ) => {
    await APIManager.setGlobalAdminFlexSubmissionAPIData(
      appId,
      submissionId,
      updatedSubmissionAPIdata
    );

    dispatch(setGlobalFlexMetaDataChipList(updatedFlexMetaChipList));
    dispatch(setSubmissionAPIdata(updatedSubmissionAPIdata));
    dispatch(setSubmissionFlexMetadataIds(updatedFlexMetadataIds));
  };

  // Helper: Check if item already has metadata
  const isMetadataAlreadyAdded = useCallback((itemId) => {
    return (
      (globalFlexMetaDataChipList || []).some((c) => c.id === itemId) ||
      (flexMetadataIds || []).includes(itemId)
    );
  }, [globalFlexMetaDataChipList, flexMetadataIds]);

  // Helper: Handle existing selection logic
  const handleExistingSelection = useCallback(async () => {
    const trimmedName = (flexMetaName || "").trim();
    const selected = (globalFlexMetadata || []).find(
      (i) => (i?.name || i?.title) === trimmedName
    );

    if (!selected) {
      return;
    }

    if (isMetadataAlreadyAdded(selected.id)) {
      if (onDuplicate) onDuplicate();
      return;
    }

    const metadatDetails = globalFlexMetadata.filter(
      (data) => data.id === selected.id
    );

    if (!metadatDetails || metadatDetails.length === 0) {
      return;
    }

    const updatedFlexMetaChipList = [
      ...globalFlexMetaDataChipList,
      metadatDetails[0],
    ];

    const updatedFlexMetadataIds = [
      ...flexMetadataIds,
      metadatDetails[0].id,
    ];

    const updatedSubmissionAPIdata = {
      ...submissionAPIdata,
      flexMetadata: updatedFlexMetadataIds,
    };

    await createGlobalFlexMetaChips(
      updatedSubmissionAPIdata,
      updatedFlexMetaChipList,
      updatedFlexMetadataIds
    );
  }, [
    flexMetaName,
    globalFlexMetadata,
    isMetadataAlreadyAdded,
    onDuplicate,
    globalFlexMetaDataChipList,
    flexMetadataIds,
    submissionAPIdata,
    createGlobalFlexMetaChips,
  ]);

  // Helper: Handle new metadata creation logic
  const handleNewMetadataCreation = useCallback(async () => {
    const createPayload = {
      name: flexMetaName.trim(),
      description: flexDesc.trim(),
      domain: METADATA_DOMAIN,
    };

    const newFlexMetaData = await APIManager.createGlobalAdminFlexMetadata(
      createPayload
    );

    const updatedFlexMetadataIds = [
      ...(flexMetadataIds || []),
      newFlexMetaData.id,
    ];

    const updatedSubmissionAPIdata = {
      ...submissionAPIdata,
      flexMetadata: updatedFlexMetadataIds,
    };

    const updatedFlexMetaChipList = [
      ...globalFlexMetaDataChipList,
      newFlexMetaData,
    ];

    await createGlobalFlexMetaChips(
      updatedSubmissionAPIdata,
      updatedFlexMetaChipList,
      updatedFlexMetadataIds
    );
  }, [
    flexMetaName,
    flexDesc,
    flexMetadataIds,
    submissionAPIdata,
    globalFlexMetaDataChipList,
    createGlobalFlexMetaChips,
  ]);

  // Helper: Calculate button disabled state
  const getButtonDisabled = useMemo(() => {
    if (isEditMode) {
      return isGlobalAdmin ? !(flexDesc || "").trim() : false;
    }

    if (isExistingSelection) {
      return !(flexMetaName || "").trim();
    }

    const isNameEmpty = !(flexMetaName || "").trim();
    const isDescEmpty = isGlobalAdmin && !(flexDesc || "").trim();
    const isNameInvalid = !isNamePatternValid;

    return isNameEmpty || isDescEmpty || isNameInvalid;
  }, [isEditMode, isGlobalAdmin, flexDesc, isExistingSelection, flexMetaName, isNamePatternValid]);

  // Helper: Calculate button text based on mode
  const getButtonText = useMemo(() => {
    if (isEditMode) {
      return "Update";
    }
    if (isExistingSelection) {
      return "Save";
    }
    return "Create";
  }, [isEditMode, isExistingSelection]);

  // Helper: Get dialog title based on mode
  const getDialogTitle = useMemo(() => {
    return isEditMode ? "Edit Flex Metadata" : "Add New Flex Metadata";
  }, [isEditMode]);

  const handleSaveAddFlexData = async () => {
    if (isGlobalAdminPortal()) {
      if (isExistingSelection) {
        await handleExistingSelection();
      } else {
        await handleNewMetadataCreation();
      }
    } else {
      onSave(flexMetaName.trim(), flexDesc.trim());
    }

    handleClose();
  };

  const handleClose = () => {
    setFlexMetaName("");
    setFlexDesc("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 500,
          fontSize: "24px",
        }}
      >
        {getDialogTitle}
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, mt: 1 }}>
          <Typography sx={{ fontWeight: 500, mb: 1, mt: 1, fontSize: "16px" }}>
            Flex Metadata Name <span style={{ color: REQUIRED_FIELD_COLOR }}>*</span>
          </Typography>
          <Autocomplete
            freeSolo
            options={nameOptions}
            value={flexMetaName}
            onChange={(_, val) => {
              if (isEditMode) return;
              setFlexMetaName(val || "");
            }}
            onInputChange={(_, val) => {
              if (isEditMode) return;
              setFlexMetaName(val || "");
            }}
            disabled={isEditMode}
            slotProps={{
              listbox: {
                style: { maxHeight: LISTBOX_MAX_HEIGHT, overflowY: "auto" },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Eg: ICON_DOLBY"
                error={!isEditMode && !!nameErrorText}
                helperText={isEditMode ? "" : nameErrorText}
                slotProps={{
                  htmlInput: {
                    ...params.inputProps,
                    readOnly: isEditMode,
                  },
                }}
              />
            )}
          />  
        </Box>
        {isGlobalAdmin &&(
        <Box sx={{ mt: 3 }}>
          <Typography sx={{ fontWeight: 500, mb: 1, fontSize: "16px" }}>
            Description <span style={{ color: REQUIRED_FIELD_COLOR }}>*</span>
          </Typography>
          <TextareaAutosize
            minRows={TEXTAREA_MIN_ROWS}
            placeholder="Prompt text"
            value={flexDesc}
            onChange={(_) => setFlexDesc(_.target.value)}
            disabled={isEditMode || isExistingSelection}
            style={{
              width: "100%",
              boxSizing: "border-box",
              border: `1px solid ${BORDER_COLOR}`,
              borderRadius: 4,
              padding: "12px 14px",
              fontSize: 16,
              fontFamily: "inherit",
              color: "inherit",
              lineHeight: 1.5,
              resize: "vertical",
              overflowX: "hidden",
            }}
          />
        </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "flex-end", pb: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            fontWeight: 500,
            fontSize: "14px",
            fontFamily: "Roboto",
            background: CANCEL_BG_COLOR,
            color: "#222",
            borderRadius: "6px",
            px: 4,
            mr: 1,
            textTransform: "none",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSaveAddFlexData}
          variant="contained"
          sx={{
            ml: 2,
            background: BUTTON_BG_COLOR,
            fontWeight: 500,
            fontSize: "14px",
            fontFamily: "Roboto",
            borderRadius: "6px",
            px: 4,
            textTransform: "none",
          }}
          disabled={getButtonDisabled}
        >
          {getButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AddFlexMetaDataDialog.propTypes = {
  appId: PropTypes.string,
  submissionId: PropTypes.string,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  onDuplicate: PropTypes.func,
  flexMetaDataState: PropTypes.array,
  flexNameOptions: PropTypes.array,
  isEditMode: PropTypes.bool,
  initialName: PropTypes.string,
  initialDescription: PropTypes.string,
};

export default AddFlexMetaDataDialog;
